<script setup lang="ts">
  import type { Address } from '@/types/nest/customer'
  import { FormInput, DropDown } from '@logistiek-zonder-papier/lzp-library'
  import { watch } from 'vue'
  import { useAddressStore } from '@/store/address.store'

  const address = defineModel<Address>({ required: true })
  const addressStore = useAddressStore()

  const { saved, isEditable = true } = defineProps<{
    saved: Address
    isEditable?: boolean
    errors?: Partial<Record<keyof Address, string>>
  }>()

  const emit = defineEmits<{
    hasChanged: [value: boolean]
  }>()

  const addressKeys: (keyof Address)[] = [
    'postalCode',
    'city',
    'street',
    'houseNumber',
    'country',
    'suffix',
  ]

  watch(
    address,
    current => {
      emit(
        'hasChanged',
        addressKeys.some(key => current[key] !== saved[key])
      )
    },
    { deep: true }
  )
</script>

<template>
  <DropDown
    v-if="addressStore.addresses && !isEditable"
    :data="addressStore.addresses"
    v-model="address"
    label="Address Selector"
    required
  >
    <template #item="{ item }">
      <div v-if="item" class="address-line">
        <span> {{ item.street }} {{ item.houseNumber }}</span>
        <span> {{ item.postalCode }} {{ item.city }} </span>
        <span> {{ item.country }} </span>
      </div>
    </template>
  </DropDown>

  <FormInput
    label="Street"
    v-model="address.street"
    :disabled="!isEditable"
    :error="errors && errors.street"
  />
  <div class="postal-code">
    <FormInput
      label="Postal Code"
      class="postal"
      v-model="address.postalCode"
      :disabled="!isEditable"
      :error="errors && errors.postalCode"
    />
    <FormInput
      label="Housenumber"
      class="number"
      v-model="address.houseNumber"
      :disabled="!isEditable"
      :error="errors && errors.houseNumber"
    />
    <FormInput
      label="Suffix"
      class="number-suffix"
      v-model="address.suffix"
      :disabled="!isEditable"
    />
  </div>
  <div class="city-country">
    <FormInput
      label="City"
      class="city"
      v-model="address.city"
      :disabled="!isEditable"
      :error="errors && errors.city"
    />
    <FormInput
      label="Country"
      class="country"
      v-model="address.country"
      :disabled="!isEditable"
      :error="errors && errors.country"
    />
  </div>
</template>

<style scoped lang="css">
  .address-line {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: var(--spacing-s);
    width: 100%;
  }

  .postal-code {
    width: 100%;
    display: flex;
    flex-direction: row;
    gap: var(--spacing-s);

    .postal {
      flex: 2;
    }

    .number {
      flex: 1;
    }

    .number-suffix {
      flex: 1;
    }
  }

  .city-country {
    width: 100%;
    display: flex;
    flex-direction: row;
    gap: var(--spacing-s);

    .city {
      flex: 1;
    }

    .country {
      flex: 1;
    }
  }
</style>
